import { ref, type Ref } from 'vue'

declare const Vapor: {
    store(file: File, options?: { visibility?: string; progress?: (progress: number) => void }): Promise<VaporUploadResponse>
}

export interface VaporUploadResponse {
    key: string
    uuid: string
    bucket: string
    'content-type': string
    extension: string
    name: string
    size: number
}

export interface UploadOptions {
    filename?: string | null
    visibility?: string
    onProgress?: ((progress: number) => void) | null
    onSuccess?: ((response: VaporUploadResponse) => void) | null
    onError?: ((error: unknown) => void) | null
}

export interface UploadImageResult {
    key: string
    dataUrl: string
}

export function useFileUpload() {
    const isUploading: Ref<boolean> = ref(false)
    const uploadProgress: Ref<number> = ref(0)

    /**
     * Upload a file to S3 using Vapor.
     *
     * @param file - File, Blob, or dataURL to upload
     * @param options - Upload options
     * @returns The S3 key of the uploaded file
     */
    const uploadToS3 = async (file: File | Blob | string, options: UploadOptions = {}): Promise<string> => {
        const {
            filename = null,
            visibility = 'private',
            onProgress = null,
            onSuccess = null,
            onError = null,
        } = options

        try {
            isUploading.value = true
            uploadProgress.value = 0

            let fileToUpload: File | Blob = file as File | Blob

            // Convert dataURL to a File before uploading
            if (typeof file === 'string' && file.startsWith('data:')) {
                const response = await fetch(file)
                const blob = await response.blob()

                const mimeType = blob.type
                const extension = mimeType.split('/')[1] || 'png'
                const defaultFilename = filename ?? `upload.${extension}`

                fileToUpload = new File([blob], defaultFilename, { type: mimeType })
            }

            const uploadResponse = await Vapor.store(fileToUpload as File, {
                visibility,
                progress: (progress: number) => {
                    const progressPercent = Math.round(progress * 100)
                    uploadProgress.value = progressPercent
                    onProgress?.(progressPercent)
                },
            })

            onSuccess?.(uploadResponse)

            return uploadResponse.key

        } catch (error) {
            console.error('File upload error:', error)
            onError?.(error)
            throw error
        } finally {
            isUploading.value = false
        }
    }

    /**
     * Upload a dataURL image to S3.
     *
     * @param dataUrl - The dataURL to upload
     * @param options - Upload options
     * @returns The S3 key and original dataURL
     */
    const uploadImageDataUrl = async (dataUrl: string, options: UploadOptions = {}): Promise<UploadImageResult> => {
        if (!dataUrl || !dataUrl.startsWith('data:')) {
            throw new Error('Invalid dataURL provided')
        }

        const s3Key = await uploadToS3(dataUrl, {
            filename: options.filename ?? 'image.png',
            visibility: options.visibility ?? 'private',
            onProgress: options.onProgress,
            onSuccess: options.onSuccess,
            onError: options.onError,
        })

        return {
            key: s3Key,
            dataUrl,
        }
    }

    /**
     * Reset upload state.
     */
    const resetUploadState = (): void => {
        isUploading.value = false
        uploadProgress.value = 0
    }

    return {
        isUploading,
        uploadProgress,
        uploadToS3,
        uploadImageDataUrl,
        resetUploadState,
    }
}
